import { Store } from '@ngxs/store';
import { EngineService } from 'src/app/engine/engine.service';
import { Procedure } from '../define-procedure';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { PickModel } from 'src/app/engine/models/pick.model';
import { MessageEnum } from '../models/message.enum';
import { HitModel } from 'src/app/engine/models/hit.model';
import { EngineState } from 'src/app/engine/state/engine.state';
import {
  ToggleVisibleNodes,
  SetCheckedNodes,
  ResetEngineNodes
} from 'src/app/engine/state/nodes/engine.nodes.actions';
import { RPCEvent } from '../define-event';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { LoadStory, BuildStory } from 'src/app/story/state/story.actions';
import { StoryState } from 'src/app/story/state/story.state';
import { PaintService } from 'src/app/engine/paint.service';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { SceneIO } from '../models/scene.io';
import { CameraService } from 'src/app/engine/camera.service';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { Observable } from 'rxjs';
import { SetCamera, ZoomCameraStatic } from 'src/app/engine/state/camera/engine.camera.actions';
import { SetLabels, ResetLabels } from 'src/app/story/state/labels/story.labels.actions';
import { first } from 'rxjs/operators';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { SetChapterBackground, SelectChapterById } from 'src/app/story/state/chapters/story.chapters.actions';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { SelectedObjectUtils } from 'src/app/shared/selected-object.utils';
import { ViewActions } from 'src/app/view/state/view.actions';
import { FlyToObject, SetActiveTool, SetXrayMode, SetXrayModeCacheColorRecipes } from 'src/app/engine/state/engine.actions';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { ViewState } from 'src/app/view/state/view.state';
import { _3DUtils } from 'src/app/shared/libraries/3d-utils/3d-utils';
import { appDefaultBackground } from 'src/app/dialogs/components/background-color-dialog/background-config';
import { HistoryService } from 'src/app/history/history.service';
import { SceneAPIUtils } from './scene-utils';
import { disableXrayMode } from 'src/app/shared/xray-mode';



declare const Human: any;

function formatPickResult(hit: HitModel): SceneIO.ScenePickResponse {
  if (!hit) {
    return null;
  }

  return {
    objectId: hit.objectId,
    canvasPosition: {
      x: hit.canvasPos[0],
      y: hit.canvasPos[1]
    },
    mode: 'query',
    position: hit.worldPos
      ? {
          x: hit.worldPos[0],
          y: hit.worldPos[1],
          z: hit.worldPos[2]
        }
      : undefined
  };
}

function buildSceneInfoResponse(
  storyDisplayName: string,
  objectIds: string[],
  store: Store,
  graphService: BdGraphService
): SceneIO.SceneInfoResponse {
  
  const selectedNodes = SelectedObjectUtils.selectedNodesAsDict(store);
  const viewGraph = store.selectSnapshot(EngineState.getViewGraph);

  const humanObjectInfo = objectId => {
    const humanObject = Human.scene.objects[objectId];

    // if the object isn't a leaf, don't include a selected property for it!
    const selected = humanObject.isLeaf()
      ? SelectedObjectUtils.isObjectSelected(objectId, selectedNodes, viewGraph, graphService)
      : undefined;

    // FIXME: Heads up for future readers:
    // The postmessage layer (Human API) is filtering properties that are undefined.
    // So even though the key is present in this function's return val,
    // it's being stripped before the client gets it.
    // We will not stand for censorship! Free Hong Kong!

    return {
      objectId: humanObject.objectId,
      name: humanObject.displayName,
      description: humanObject.description || '',
      shown: humanObject.shown,
      selected: selected,
      children: humanObject.objects.map(object => object.objectId),
      parent: humanObject.parent ? humanObject.parent.objectId : null
    };
  };

  const objects = objectIds.reduce((acc, objectId) => {
    acc[objectId] = humanObjectInfo(objectId);
    return acc;
  }, {});

  return {
    name: storyDisplayName,
    objects
  };
}

function captureObjects(store: Store, graphService: BdGraphService): SceneIO.ObjectCaptureInfo[] {
  const selectedNodes = SelectedObjectUtils.selectedNodesAsDict(store);
  const viewGraph = store.selectSnapshot(EngineState.getViewGraph);
  const objectIds = Object.keys(Human.scene.objects);

  return objectIds.map(objectId => {
    const hidden = (() => {
      const shown = Human.scene.objects[objectId].shown;
      switch (shown) {
        case undefined: return undefined;
        default: return !shown;
      }
    })();
    
    return {
      objectId,
      selected: SelectedObjectUtils.isObjectSelected(objectId, selectedNodes, viewGraph, graphService),
      hidden
    };
  });
}

function loadStory(store: Store, storyId: string) {
  const type: StoryType = storyId.includes('/')
    ? StoryType.Module
    : StoryType.Bookmark;
  store.dispatch(new LoadStory(storyId, type));
}



function captureScene(
  store: Store,
  graphService: BdGraphService,
  cameraService: CameraService
): SceneIO.SceneCapture {
  // wrangle state from the store or engine
  const camera = cameraService.captureCamera();
  const objects = captureObjects(store, graphService);
  const background = store
    .selectSnapshot(StoryChaptersState.getStoryChapters)
    .map(chapter => {
      return {
        chapterId: chapter.id,
        background: chapter.background || appDefaultBackground
      };
    });


  // no transformations necessary for this capture data
  const storyId = store.selectSnapshot(StoryState.getStoryId);
  const labels = store.selectSnapshot(StoryLabelsState.getLabels);
  const activeChapterId = store.selectSnapshot(
    StoryChaptersState.getSelectedChapterId
  );

  const sceneCapture: SceneIO.SceneCaptureResponse = {
    camera,
    labels,
    storyId,
    objects,
    background,
    activeChapterId
  };

  return sceneCapture;
}

function applyCapturedState(
  capture: SceneIO.SceneCapture,
  store: Store,
  engineService: EngineService,
  isLegacyCapture = false
) {
  return new Promise(resolve => {
    // handle selecting and hiding objects
    const viewGraph = store.selectSnapshot(EngineState.getViewGraph);
    const getNode = (object: SceneIO.ObjectCaptureInfo) =>
      engineService.getNode(viewGraph, object.objectId);

    const checkedNodes = capture.objects
      .filter(object => object.selected === true)
      .map(getNode);


    // handle old capture format, where objects that are hidden are EXCLUDED from capture.objects
    // HACK the old format removed objects from the capture if they were hidden
    // so, if an object in the scene is missing from the capture, it should be toggled off.
    const hiddenNodes = (() => {
      if (isLegacyCapture) {
        const hiddenNodesLegacy = Human.scene.getLeaves()
          .filter(objectId => {
            const captureObject = capture.objects.find(object => object.objectId === objectId);
            const missingFromCapture = captureObject === undefined;
            return missingFromCapture;
          })
          .map(objectId => getNode({ objectId } as SceneIO.ObjectCaptureInfo))
          .filter(x => x);
        return hiddenNodesLegacy;

      } else {
        return capture.objects
          .filter(object => object.hidden === true)
          .map(getNode)
          .filter(x => x); // some objects aren't in the view graph, so we have to filter them.
      }
    })();

    // handle background model for each chapter
    const setBackgroundActions = capture.background.map(
      backgroundCapture =>
        new SetChapterBackground(
          backgroundCapture.chapterId,
          backgroundCapture.background
        )
    );

    const selectChapterAction = capture.activeChapterId 
      ? [new SelectChapterById(capture.activeChapterId)]
      : [];

    const zoomSet = capture.camera.zoom !== undefined 
      ? [new ZoomCameraStatic(capture.camera.zoom)]
      : [];

    // set Timeout, so that nodes are highlighted
    setTimeout(() => {
      store.dispatch([
        new SetCamera(capture.camera.lookAt, false),
        new SetLabels(capture.labels),
        new ToggleVisibleNodes(hiddenNodes, false),
        new SetCheckedNodes(checkedNodes),
        ...zoomSet,
        ...selectChapterAction,
        ...setBackgroundActions
      ]);
      resolve();
    });
  });
}

function restoreScene(
  capture: SceneIO.SceneCapture,
  store: Store,
  engineService: EngineService,
  isLegacyCapture: boolean
): Observable<true> {
  return new Observable(observer => {
    engineService
      .getEvent(EventsEnum.HumanStarted)
      .pipe(first())
      .subscribe(() => {
        applyCapturedState(capture, store, engineService, isLegacyCapture).then(
          () => {
            engineService.fireEvent(EventsEnum.SceneRestored, undefined);
            observer.next(true);
            observer.complete();
          }
        );
      });
    loadStory(store, capture.storyId);
  });
}

function focusObject(
  params: SceneIO.FocusObjectRequest,
  store: Store,
  paintService: PaintService,
  engineService: EngineService
) {
  // enable xray mode if something is selected
  if (params.select && params.xray) {
    enableXrayMode(store, paintService, engineService);
  } else {
    disableXrayMode(store, engineService);
  }

  // set selected
  if (params.select) {
    setObjectSelected({ [params.objectId]: true }, store, engineService);
  }

  // flyTo object
  const animate = true;
  const animationDuration = 1;
  store.dispatch(new FlyToObject(params.objectId, animate, animationDuration));
}


export function getSceneEvents(): RPCEvent<SceneIO.SceneEventResponse>[] {
  return [
    {
      eventName: EventsEnum.ScenePicked,
      aliasEvents: [EventsEnum.PickPicked],
      mapFn: formatPickResult
    },
    {
      eventName: EventsEnum.SceneObjectsSelected,
      mapFn: (event) => event.selectedObjectsUpdate 
    },
    {
      eventName: EventsEnum.SceneObjectsShown,
      mapFn: (event) => event.enabledObjectsUpdate
    },
    {
      eventName: EventsEnum.Debug_SceneReady,
      aliasEvents: [EventsEnum.Started]
    },
    {
      eventName: EventsEnum.SceneRestored,
      mapFn: () => true
    },
    {
      eventName: EventsEnum.SceneXrayEnabled,
      mapFn: () => true
    },
    {
      eventName: EventsEnum.SceneXrayDisabled,
      mapFn: () => true
    }
  ];
}


function setObjectSelected(
  params: SceneIO.SelectObjectsRequest, 
  store: Store, 
  engineService: EngineService
  ): true {
  const viewGraph = store.selectSnapshot(EngineState.getViewGraph);

  const selectedNodes = Object.keys(params)
    .filter(objectId => viewGraph.nodes[objectId] !== undefined) // sanitize: filter objects that we don't know about.
    .filter(objectId => params[objectId] === true)
    .map(objectId => engineService.getNode(viewGraph, objectId));

  store.dispatch(new SetCheckedNodes(selectedNodes));

  return true;
}

function enableXrayMode(
  store: Store,
  paintService: PaintService,
  engineService: EngineService
): true {
  const colorRecipes = engineService.getLeaves().map((leafId: string) => {
    return {
      objectId: leafId,
      recipe: paintService.getColor(leafId)
    };
  });

  store.dispatch(new SetXrayModeCacheColorRecipes(colorRecipes));
  store.dispatch(new SetXrayMode(true));
  engineService.fireEvent(EventsEnum.SceneXrayEnabled, undefined);
  return true;
}



export function getSceneProcedures(
  store: Store,
  engineService: EngineService,
  paintService: PaintService,
  cameraService: CameraService,
  graphService: BdGraphService,
  historyService: HistoryService
): Procedure<SceneIO.SceneProcRequest, SceneIO.SceneProcResponse>[] {
  return [
    {
      // legacy, keeping support for now
      procName: MessageEnum.focusObject,
      actionFn: (params: SceneIO.FocusObjectRequest) => {
        focusObject(params, store, paintService, engineService);
        return true;
      }
    },
    {
      procName: MessageEnum.dissectSetEnabled,
      actionFn: (params: SceneIO.DissectSetEnabledRequest) => {
        const tool = params.enable ? ToolEnum.Hide : ToolEnum.Select;
        store.dispatch(new SetActiveTool(tool));
        return true;
      }
    },
    {
      procName: MessageEnum.sceneSetClickMode,
      actionFn: (mode: SceneIO.SceneSetClickModeRequest) => {

        const tool = (() => {
          if (mode === 'select') { return ToolEnum.Select; }
          if (mode === 'hide') { return ToolEnum.Hide; }
          if (mode === 'fade') { return ToolEnum.Fade; }
          return ToolEnum.Select;
        })();

        store.dispatch(new SetActiveTool(tool));
        return true;
      }
    },
    {
      procName: MessageEnum.pickSingleSetEnabled,
      actionFn: (params: SceneIO.PickSingleSetEnabledRequest) => {
        const enable = (() => {
          switch (params.enable) {
            case undefined: return !store.selectSnapshot(ViewState.getMultiPickModeEnabled);
            case true: return false;
            case false: return true;
          }
        })();
        store.dispatch(new ViewActions.SetMultiPickModeEnabled(enable));
        return true;
      }
    },
    {
      procName: MessageEnum.pickMultiSetEnabled,
      actionFn: (params: SceneIO.PickMultiSetEnabledRequest) => {
        const enable = (() => {
          switch (params.enable) {
            case undefined: return !store.selectSnapshot(ViewState.getMultiPickModeEnabled);
            default: return params.enable;
          }
        })();
        store.dispatch(new ViewActions.SetMultiPickModeEnabled(enable));
        return true;
      }
    },
    {
      procName: MessageEnum.getCanvasPos,
      actionFn: (params: SceneIO.SceneGetCanvasPosRequest) => {
        const canvas = engineService.getCanvas();
        return _3DUtils.getCanvasPos(
          Human,
          params.worldPos,
          params.offset,
          canvas.width,
          canvas.height
        );
      }
    },
    {
      procName: MessageEnum.SceneCapture,
      actionFn: () => captureScene(store, graphService, cameraService)
    },
    {
      procName: MessageEnum.SceneRestore,
      actionFn: (capture: SceneIO.SceneRestoreRequest) => {

        const isLegacyCapture = SceneAPIUtils.isLegacyCaptureFormat(capture);

        const sanitizedCapture = isLegacyCapture
          ? SceneAPIUtils.convertCaptureFormat(capture as SceneIO.LegacySceneCapture)
          : capture as SceneIO.SceneCapture;

        return restoreScene(
          sanitizedCapture,
          store,
          engineService,
          isLegacyCapture,
        );
      }
    },
    {
      procName: MessageEnum.SceneEnableXrayMode,
      actionFn: () => enableXrayMode(store, paintService, engineService)
    },
    {
      procName: MessageEnum.SceneDisableXrayMode,
      actionFn: () => disableXrayMode(store, engineService)
    },
    {
      procName: MessageEnum.SceneLoad,
      actionFn: (id: SceneIO.SceneLoadRequest) => {
        loadStory(store, id);
        return true;
      }
    },
    { 
      procName: MessageEnum.SceneReset,
      actionFn: () => {
        historyService.resetChapter();
        return true;
      }
    },
    {
      procName: MessageEnum.SceneInfo,
      actionFn: (params: SceneIO.SceneInfoRequest) => {
        const objectIds =
          params && params.objectId
            ? [params.objectId]
            : Object.keys(Human.scene.objects);
            
        const displayName = store.selectSnapshot(StoryState.getStory).displayName;
        return buildSceneInfoResponse(displayName, objectIds, store, graphService);
      }
    },
    {
      procName: MessageEnum.ScenePick,
      actionFn: (params: SceneIO.ScenePickRequest) => {
        
        const pick: PickModel = {
          x: params.x,
          y: params.y,
          rayPick: true
        };

        const hit = engineService.pickScene(pick);
        return formatPickResult(hit);
      }
    },
    {
      procName: MessageEnum.SceneSelectObjects,
      actionFn: (params: SceneIO.SelectObjectsRequest) => setObjectSelected(params, store, engineService)
    },
    {
      procName: MessageEnum.SceneShowObjects,
      actionFn: (params: SceneIO.ShowObjectsRequest) => {
        engineService.setNodesVisible(params);
        return true;
      } 
    },
    {
      // only included to support migration away from scene.selectionMode
      procName: MessageEnum.IsolateObject,
      actionFn: (objectId: SceneIO.SceneIsolateObjectRequest) => {

        // hide all objects
        // show object in params

        const objects = {};

        for (const leafId of Human.scene.getLeaves()) {
          objects[leafId] = false; // do not show
        }

        objects[objectId] = true;

        engineService.setNodesVisible(objects);
        return true;
      } 
    },
    {
      procName: MessageEnum.SceneTransformObject,
      actionFn: (params: SceneIO.TransformObjectRequest) => {
        engineService.transformObject(params.objectId, params);
        return true;
      } 
    },
    {
      procName: MessageEnum.SceneColorObject,
      actionFn: (params: SceneIO.ColorObjectRequest) => {
        const colorRecipe = new ColorRecipeModel(params);
        paintService.colorObject(params.objectId, colorRecipe);
        return true;
      } 
    },
    {
      procName: MessageEnum.SceneGetColor,
      actionFn: (params: SceneIO.SceneColorRequest) => {
        return paintService.getColor(params.objectId) as SceneIO.ObjectColorResponse;
      } 
    },
    {
      procName: MessageEnum.SceneUncolorObject,
      actionFn: (params: SceneIO.SceneColorRequest) => {
        paintService.uncolorObject(params.objectId);
        return true;
      } 
    },
    { 
      procName: MessageEnum.SceneDisableHighlight,
      actionFn: () => {
        paintService.disableHighlight();
        return true;
      }
    },
    { 
      procName: MessageEnum.SceneEnableHighlight,
      actionFn: () => {
        paintService.enableHighlight();
        return true;
      }
    }
  ];
}
